import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, createVNode as _createVNode, withCtx as _withCtx, createSlots as _createSlots } from "vue"

import { computed, onBeforeMount, ref, watch } from 'vue'

import DocLayout from '@/app/components/doc/Layout.vue'
import PrevNext from '@/app/components/doc/PrevNext.vue'
import Chapter from '@/app/components/doc/reading/Chapter.vue'
import Exercise from '@/app/components/doc/reading/Exercise.vue'
import EarlyAccessBannerBook from '@/app/components/doc/EarlyAccessBannerBook.vue'
import EarlyAccessBannerPodcast from '@/app/components/doc/EarlyAccessBannerPodcast.vue'

import AudioPlayer from '@/app/components/doc/player/Player.vue'
import AudioPlayerDisabled from '@/app/components/doc/player/PlayerDisabled.vue'

import { emitter } from '@/services/mitt'
import type { DocView } from '@/models/doc'
import type { AnnotationWithContent } from '@/models/interfaces'
import { backend } from '@/services/backend'
import { playerSession } from '@/services/player'
import { onBeforeRouteUpdate, useRoute } from 'vue-router'
import type { RouteLocationNormalized } from 'vue-router'
import { Sentry } from '@/services/sentry'
import {
  aiLabel13537ExperimentVariant,
  miniPlayer19040Experiment,
} from '@/services/ab'
import { Mixpanel } from '@/analytics/mixpanel'


export default /*@__PURE__*/_defineComponent({
  __name: 'Content',
  props: {
    doc: {}
  },
  setup(__props: any) {

const props = __props

const route = useRoute()
const targetHighlight = ref<AnnotationWithContent | undefined>(undefined)
// The current page ID whose saved highlight spans have been rendered.
// This needs to match `doc.currentPage.id` before we scroll to a highlight.
const pageIdForCurrentlyRenderedHighlights = ref<string | undefined>(undefined)

onBeforeMount(async () => {
  await handleHighlight(route)
  Mixpanel.setFirstDoc('Viewed', props.doc.title, props.doc.doc_type)
  await miniPlayer19040Experiment()
})

/**
 * Hand over the player session from the Mini Player to the full player if the
 * it was playing the current page's doc. Otherwise, reset the player session.
 *
 * We watch the doc rather than hook the mount: the page keeps its component
 * when the reader moves from one doc to another.
 */
watch(
  () => props.doc,
  async (doc: DocView) => {
    if (playerSession.value.isLoaded(doc)) {
      // The doc was opened from the Mini Player. Open the full player.
      doc.controls.showPlayer()
    } else {
      // Otherwise, just load the doc in the player session.
      await playerSession.value.prepareDoc(doc)
    }
  },
  { immediate: true },
)

onBeforeRouteUpdate(async (to: RouteLocationNormalized) => {
  await handleHighlight(to)
})

function onDocContentClick(event: any): void {
  emitter.emit('docContentClicked', event)
}

/**
 * Fetch a highlight by ID if the `highlight` query param is present.
 */
async function _maybeFetchHighlight(
  route: RouteLocationNormalized,
): Promise<AnnotationWithContent | undefined> {
  const highlightId = route.query?.highlight as string | undefined
  if (!highlightId) {
    return undefined
  }
  try {
    return await backend.getHighlight(highlightId)
  } catch (e) {
    Sentry.captureException(e)
    return undefined
  }
}

const showBookBanner = computed(() => {
  return props.doc.doc_type === 'book' && aiLabel13537ExperimentVariant()
})

/**
 * Try to fetch the highlight in the query param, set it as our target
 * highlight, and scroll to it if it's rendered.
 */
async function handleHighlight(route: RouteLocationNormalized): Promise<void> {
  const highlight = await _maybeFetchHighlight(route)
  targetHighlight.value = highlight
  scrollToTargetHighlight()
}

/**
 * Once the annotator emits `highlightsRendered`, mark that this page's
 * highlights have been rendered, and scroll to the highlight we are waiting
 * for the rendering of (if any).
 */
function onHighlightsRendered(): void {
  pageIdForCurrentlyRenderedHighlights.value = props.doc.currentPage.id
  scrollToTargetHighlight()
}

/**
 * Scrolls immediately when the target highlight is already rendered; otherwise
 * return, leaving it pending until the annotator emits `highlightsRendered`
 * for the current page.
 */
function scrollToTargetHighlight(): void {
  const highlight = targetHighlight.value
  if (!highlight) {
    return
  }

  if (
    pageIdForCurrentlyRenderedHighlights.value !== props.doc.currentPage.id &&
    !isHighlightInDom(highlight)
  ) {
    return
  }

  props.doc.scrollToHighlight(highlight)
  targetHighlight.value = undefined
}

function isHighlightInDom(highlight: AnnotationWithContent): boolean {
  return Boolean(
    document.querySelector(`[data-annotation-id="${highlight.id}"]`),
  )
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(DocLayout, { doc: __props.doc }, _createSlots({
    "page-content": _withCtx(() => [
      _createElementVNode("div", {
        class: _normalizeClass(["book-reading__content-editor content-editor", __props.doc.settings.cssClass]),
        style: _normalizeStyle({ 'font-family': __props.doc.settings.fontFamily }),
        onClick: onDocContentClick
      }, [
        (__props.doc.isCurrentPageChapter)
          ? (_openBlock(), _createBlock(Chapter, {
              key: __props.doc.currentPage.id,
              content: __props.doc.currentPage,
              class: "chapter-text",
              onHighlightsRendered: onHighlightsRendered
            }, null, 8, ["content"]))
          : (__props.doc.isCurrentPageOpenQuestions)
            ? (_openBlock(), _createBlock(Exercise, {
                key: 1,
                content: __props.doc.currentExercise,
                isEOC: __props.doc.currentExercise.currentExercise.isExerciseEOC,
                class: "chapter-exercise"
              }, null, 8, ["content", "isEOC"]))
            : _createCommentVNode("", true)
      ], 6),
      _createVNode(PrevNext, { doc: __props.doc }, null, 8, ["doc"])
    ]),
    "page-footer": _withCtx(() => [
      (__props.doc.audio && __props.doc.controls.isPlayerRendered)
        ? (_openBlock(), _createBlock(AudioPlayer, {
            key: 0,
            doc: __props.doc
          }, null, 8, ["doc"]))
        : _createCommentVNode("", true),
      (!__props.doc.audio && __props.doc.has_audio && __props.doc.controls.isPlayerRendered)
        ? (_openBlock(), _createBlock(AudioPlayerDisabled, {
            key: 1,
            class: _normalizeClass(__props.doc.controls.playerClass)
          }, null, 8, ["class"]))
        : _createCommentVNode("", true)
    ]),
    _: 2
  }, [
    (__props.doc.showEarlyAccessBanner)
      ? {
          name: "banner",
          fn: _withCtx(() => [
            (showBookBanner.value)
              ? (_openBlock(), _createBlock(EarlyAccessBannerBook, { key: 0 }))
              : _createCommentVNode("", true),
            (__props.doc.doc_type === 'podcast_episode')
              ? (_openBlock(), _createBlock(EarlyAccessBannerPodcast, { key: 1 }))
              : _createCommentVNode("", true)
          ]),
          key: "0"
        }
      : undefined
  ]), 1032, ["doc"]))
}
}

})